import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, BarChart3 } from 'lucide-react';
import { safeLog } from '@/utils/safeLogger';
import { EMAIL_TYPE_LABELS } from '@/lib/emailSampleVars';

interface EmailEvent {
  id: string;
  email_type: string;
  recipient_email: string;
  status: string;
  created_at: string;
  sent_at: string | null;
  user_id: string | null;
}

export function EmailEventsLog() {
  const [events, setEvents] = useState<EmailEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvents();
  }, []);

  async function loadEvents() {
    try {
      const { data, error } = await supabase
        .from('email_events')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      safeLog.error('Error loading email events', error as Error);
    } finally {
      setLoading(false);
    }
  }

  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return (
          <Badge className="bg-success/15 text-success border border-success/30 hover:bg-success/20">
            sent
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-warning/15 text-warning border border-warning/30 hover:bg-warning/20">
            pending
          </Badge>
        );
      case 'failed':
        return <Badge variant="destructive">failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <Card className="app-tile app-tile-hover">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="app-icon-box">
              <BarChart3 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="app-section-title flex items-center gap-2">
                Email Events Log
                {!loading && events.length > 0 && (
                  <Badge variant="outline" className="font-mono tabular-nums">
                    {events.length}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>Last 100 emails sent system-wide</CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : events.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border/60 py-12 text-center text-sm text-muted-foreground">
            No emails sent yet.
          </div>
        ) : (
          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Sent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((event) => (
                  <TableRow key={event.id} className="hover:bg-muted/40">
                    <TableCell className="font-medium">
                      {EMAIL_TYPE_LABELS[event.email_type] ??
                        event.email_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </TableCell>
                    <TableCell className="text-sm">{event.recipient_email}</TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground">
                      {event.user_id ? event.user_id.slice(0, 8) : '—'}
                    </TableCell>
                    <TableCell>{renderStatusBadge(event.status)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                      {new Date(event.created_at).toLocaleString('de-DE')}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                      {event.sent_at ? new Date(event.sent_at).toLocaleString('de-DE') : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
